import React, { useEffect, useState } from "react";
import { DateTime } from "luxon";
import FileDownload from "js-file-download";
import { toast } from "react-toastify";
import financeApiService from "../../../../services/financeApiService";
import financeV2Adapters from "../../../../adapters/financeV2Adapters";
import styles from "../FinanceDetailV2.module.scss";

const formatDate = (d) => {
  if (!d) return "—";
  const dt = DateTime.fromISO(d);
  return dt.isValid ? dt.toFormat("dd/MM/yyyy") : "—";
};

const formatEuro = (n) =>
  n != null ? `${Number(n).toLocaleString("fr-FR")} €` : "—";

// LS-642 — vue lecture seule du tableau d'amortissement complet d'un devis,
// mois par mois (paliers, mensualités réelles, montants à échoir), distincte
// de la table éditable de FinanceTabV2. Ouverte via le bouton "Afficher le
// tableau d'amortissement", visible uniquement si l'échéancier a été saisi.
// Les lignes proviennent de l'API (GET .../echeancier), seule source de
// vérité partagée avec l'export xlsx (GET .../echeancier/export) — le calcul
// mois par mois n'est plus fait côté front (LS-612 → LS-642).
// LS-644 — colonne "Mensualité calculée" retirée de l'affichage : seule la
// mensualité réelle (retenue) est montrée. La valeur calculée reste dans
// `ligne.mensualiteCalculee` (non affichée), utilisée pour le calcul de
// l'écart en euros.
const EcheancierRecapModal = ({
  reference,
  demandeUuid,
  devisUuid,
  onClose,
}) => {
  const [lignes, setLignes] = useState([]);
  const [valeurResiduelleFinale, setValeurResiduelleFinale] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    if (!demandeUuid || !devisUuid) return;
    setIsLoading(true);
    financeApiService
      .getEcheancierMensuel(demandeUuid, devisUuid)
      .then((res) => {
        const data = financeV2Adapters.fromApi.echeancierMensuel(res.data);
        setLignes(data.lignes);
        setValeurResiduelleFinale(data.valeurResiduelleFinale);
      })
      .catch(() =>
        toast.error("Impossible de charger le tableau d'amortissement."),
      )
      .finally(() => setIsLoading(false));
  }, [demandeUuid, devisUuid]);

  async function handleExport() {
    setIsExporting(true);
    try {
      const res = await financeApiService.exporterEcheancierMensuel(
        demandeUuid,
        devisUuid,
      );
      // Une référence de devis peut contenir "/" (ex: "24/001"), invalide dans
      // un nom de fichier téléchargé.
      const referenceFichier = (reference || devisUuid).replace(/[/\\]/g, "-");
      FileDownload(res.data, `echeancier-${referenceFichier}.xlsx`);
    } catch {
      toast.error("L'export du tableau d'amortissement a échoué.");
    } finally {
      setIsExporting(false);
    }
  }

  return (
    <div
      className={styles.echeancierRecapOverlay}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className={styles.echeancierRecapCard}>
        <div className={styles.echeancierRecapHeader}>
          <h3>
            Tableau d&apos;amortissement
            {reference ? ` — ${reference}` : ""}
          </h3>
          <div>
            <button
              type="button"
              className={styles.echeancierRecapBtn}
              onClick={handleExport}
              disabled={isLoading || isExporting}
            >
              <i
                className={`fa fa-${isExporting ? "spinner fa-spin" : "download"} mr-1`}
              />
              {isExporting ? "Export..." : "Exporter"}
            </button>
            <button
              type="button"
              className={styles.echeancierCancelBtn}
              onClick={onClose}
            >
              <i className="fa fa-times mr-1" />
              Fermer
            </button>
          </div>
        </div>

        {isLoading ? (
          <span className={styles.financierCardStatusMuted}>Chargement…</span>
        ) : (
          <>
            <div className={styles.echeancierRecapTableWrapper}>
              <table className={styles.echeancierRecapTable}>
                <thead>
                  {/* LS-731 — libellés actés en visio du 10/09/2026 : la mensualité
                      retenue est HT, Leasecar est prélevé en TTC et contrôle la TVA
                      tous les mois. Mêmes colonnes que l'export xlsx. */}
                  <tr>
                    <th>Échéance</th>
                    <th>Palier</th>
                    <th>Date</th>
                    <th>Mensualité HT</th>
                    <th>TVA</th>
                    <th>Mensualité TTC</th>
                    <th>Écart €</th>
                    <th>Montant à échoir</th>
                  </tr>
                </thead>
                <tbody>
                  {lignes.map((ligne) => (
                    <tr key={"echeancier-recap-" + ligne.echeance}>
                      <td>{ligne.echeance}</td>
                      <td>{ligne.palierNum}</td>
                      <td>{formatDate(ligne.date)}</td>
                      <td>{formatEuro(ligne.mensualiteRetenue)}</td>
                      <td>{formatEuro(ligne.tva)}</td>
                      <td>{formatEuro(ligne.mensualiteTTC)}</td>
                      <td>
                        {ligne.ecartEuros != null ? (
                          <span
                            className={
                              ligne.ecartEuros > 0
                                ? styles.ecartNegatif
                                : styles.ecartPositif
                            }
                          >
                            {ligne.ecartEuros > 0 ? "+" : ""}
                            {ligne.ecartEuros.toLocaleString("fr-FR", {
                              maximumFractionDigits: 2,
                            })}{" "}
                            €
                          </span>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td>{formatEuro(ligne.montantAEchoir)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className={styles.echeancierRecapFooter}>
              Valeur résiduelle finale : {formatEuro(valeurResiduelleFinale)}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default EcheancierRecapModal;
